import React, { useState, useEffect } from 'react';
import { History as HistoryIcon, Clock, Loader2, Cpu, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { Surface, AiBadge } from '../calm';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HistoryPanel - Run history and audit trail
 */
const HistoryPanel = ({ caseId, onCountChange }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [caseId]);

  const fetchHistory = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/ai-workspace/runs/matter/${caseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const items = response.data.runs || [];
      setRuns(items);
      if (onCountChange) {
        onCountChange(items.length);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Kunne ikke laste historikk');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-ai-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Kjøringshistorikk
          </h3>
          <p className="text-sm text-text-secondary">
            Full audit trail av alle AI-interaksjoner
          </p>
        </div>

        {runs.length === 0 ? (
          <Surface className="text-center py-12">
            <HistoryIcon className="w-12 h-12 mx-auto mb-4 text-text-secondary" />
            <h4 className="font-semibold text-text-primary mb-2">
              Ingen historikk ennå
            </h4>
            <p className="text-sm text-text-secondary">
              AI-kjøringer vil vises her
            </p>
          </Surface>
        ) : (
          <div className="space-y-3">
            {runs.map(run => (
              <Surface key={run.id} className="!p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <AiBadge>{run.agent_profile_id}</AiBadge>
                        <span className="text-xs text-text-secondary">
                          {new Date(run.created_at).toLocaleString('no-NO')}
                        </span>
                      </div>
                      <p className="text-sm text-text-primary font-medium mb-2">
                        {run.user_prompt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-text-secondary">
                        <span className="flex items-center gap-1">
                          <Cpu className="w-3 h-3" />
                          {run.model_id}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          ${run.cost?.toFixed(4) || '0.0000'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {run.duration_ms}ms
                        </span>
                        <span>
                          {run.tokens_input + run.tokens_output} tokens
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedRun(selectedRun?.id === run.id ? null : run)}
                      className="text-sm text-ai-primary hover:underline"
                    >
                      {selectedRun?.id === run.id ? 'Skjul' : 'Vis svar'}
                    </button>
                  </div>

                  {selectedRun?.id === run.id && (
                    <div className="pt-3 border-t border-border-calm">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{run.output_text}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              </Surface>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPanel;